import { NewButton, NewButtonVariant } from "@visiblethread/react-components";
import { PHASE_MESSAGES } from "constants/outlineGeneration";
import { format } from "date-fns";
import { func } from "prop-types";
import { useSelector } from "react-redux";
import {
  selectConnectionState,
  selectCurrentMessage,
  selectCurrentPhase,
  selectDetectedConfidence,
  selectDetectedLabel,
  selectDetectedType,
  selectGenerationFailed,
  selectGenerationStartedAt,
  selectIsAwaitingReqReview,
  selectIsAwaitingTypeReview,
  selectOverallProgress,
  selectProgressError,
  selectRequirementsFeed,
  selectRequirementsFound,
} from "slices/proposalOutline/selectors";

import DetectedTypeBanner from "./DetectedTypeBanner";
import classes from "./OutlineGenerationProgress.module.scss";
import PhaseStepper from "./PhaseStepper";
import ProgressRing from "./ProgressRing";
import RequirementsFeed from "./RequirementsFeed";

const formatStarted = (startedAt) =>
  startedAt ? `Started ${format(new Date(startedAt), "h:mm a")}` : "";

const OutlineGenerationProgress = ({ onRetry }) => {
  const overallProgress = useSelector(selectOverallProgress);
  const currentPhase = useSelector(selectCurrentPhase);
  const currentMessage = useSelector(selectCurrentMessage);
  const requirementsFound = useSelector(selectRequirementsFound);
  const requirementsFeed = useSelector(selectRequirementsFeed);
  const startedAt = useSelector(selectGenerationStartedAt);
  const failed = useSelector(selectGenerationFailed);
  const progressError = useSelector(selectProgressError);
  const connectionState = useSelector(selectConnectionState);
  const isReconnecting = connectionState === "reconnecting";
  const detectedType = useSelector(selectDetectedType);
  const detectedConfidence = useSelector(selectDetectedConfidence);
  const detectedLabel = useSelector(selectDetectedLabel);
  const isAwaitingTypeReview = useSelector(selectIsAwaitingTypeReview);
  const isAwaitingReqReview = useSelector(selectIsAwaitingReqReview);
  const awaitingGate = isAwaitingTypeReview || isAwaitingReqReview;
  const statusMessage = currentMessage || PHASE_MESSAGES[currentPhase] || "";

  return (
    <div
      className={classes["progress"]}
      data-testid="outline-generation-progress"
    >
      <div className={classes["progress__header"]}>
        <div className={classes["progress__main"]}>
          <div className={classes["progress__title"]}>
            <h3>Outline Generation in Progress</h3>
            <p className={classes["progress__sub"]}>
              {formatStarted(startedAt)}
            </p>
            {isReconnecting && (
              <span
                className={classes["progress__reconnecting_pill"]}
                role="status"
              >
                Reconnecting…
              </span>
            )}
          </div>

          <PhaseStepper
            currentPhase={currentPhase}
            failed={failed}
            awaitingGate={awaitingGate}
          />

          {!failed && statusMessage && (
            <p
              className={classes["progress__message"]}
              role="status"
              data-testid="outline-progress-message"
            >
              {statusMessage}
            </p>
          )}
        </div>

        <ProgressRing progress={overallProgress} />
      </div>

      <DetectedTypeBanner
        type={detectedType}
        label={detectedLabel}
        confidence={detectedConfidence}
      />

      {failed ? (
        <div className={classes["progress__error"]} role="alert">
          <p className={classes["progress__error_msg"]}>
            {progressError || "Outline generation failed."}
          </p>
          <NewButton onClick={onRetry} variant={NewButtonVariant.l1}>
            Retry
          </NewButton>
        </div>
      ) : (
        <RequirementsFeed
          items={requirementsFeed}
          total={requirementsFound}
          isRunning={!awaitingGate}
        />
      )}
    </div>
  );
};

OutlineGenerationProgress.propTypes = {
  onRetry: func,
};

export default OutlineGenerationProgress;
